/**
 * Pure Ohm's-law concept model for the V/I/R training bench.
 *
 * Fixed low-voltage source, adjustable resistance, I = V / R.
 * No Three.js — render / station code maps outputs to lamp + needles.
 *
 * Framing: conceptual non-energized mockup values only.
 */

/** Conceptual training source (volts). */
export const CONCEPT_VOLTAGE_V = 12;

/** Continuous resistance range (ohms), low → high. */
export const RESISTANCE_MIN_OHMS = 10;
export const RESISTANCE_MAX_OHMS = 200;

/** Default knob position in [0, 1] (mid-low resistance → visible lamp). */
export const DEFAULT_KNOB_NORMALIZED = 0.35;

/** Peak current at minimum R — used to normalize meters / lamp. */
export const CURRENT_MAX_A = CONCEPT_VOLTAGE_V / RESISTANCE_MIN_OHMS;

export interface CircuitState {
  /** Source voltage (V). */
  readonly voltageV: number;
  /** Load resistance (Ω). */
  readonly resistanceOhms: number;
  /** Series current I = V / R (A). */
  readonly currentA: number;
}

/** Clamp helper kept local so the model stays dependency-free. */
function clamp01(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t;
}

/**
 * Map normalized knob position [0, 1] → resistance (Ω).
 * 0 = lowest R (brightest lamp / highest current), 1 = highest R.
 */
export function resistanceFromKnob(normalized: number): number {
  const t = clamp01(normalized);
  return RESISTANCE_MIN_OHMS + t * (RESISTANCE_MAX_OHMS - RESISTANCE_MIN_OHMS);
}

/** Inverse map: resistance (Ω) → normalized knob [0, 1]. */
export function knobFromResistance(resistanceOhms: number): number {
  const span = RESISTANCE_MAX_OHMS - RESISTANCE_MIN_OHMS;
  if (span <= 0) return 0;
  return clamp01((resistanceOhms - RESISTANCE_MIN_OHMS) / span);
}

/** Evaluate series circuit with fixed conceptual voltage. */
export function evaluateCircuit(
  resistanceOhms: number,
  voltageV: number = CONCEPT_VOLTAGE_V,
): CircuitState {
  const r = Math.max(resistanceOhms, 1e-6);
  const v = Math.max(voltageV, 0);
  return {
    voltageV: v,
    resistanceOhms: r,
    currentA: v / r,
  };
}

/** Convenience: knob [0,1] → all circuit outputs. */
export function evaluateFromKnob(
  normalized: number,
  voltageV: number = CONCEPT_VOLTAGE_V,
): CircuitState {
  return evaluateCircuit(resistanceFromKnob(normalized), voltageV);
}

/**
 * Map current → lamp brightness in [0, 1].
 * Bright at high I (low R); dim glow remains at minimum for readability.
 */
export function lampBrightnessFromCurrent(currentA: number): number {
  const t = clamp01(currentA / CURRENT_MAX_A);
  // Mild ease so mid resistances still read as "on".
  return 0.06 + t * t * 0.94;
}

/**
 * Map brightness [0,1] → MeshStandardMaterial emissiveIntensity.
 * Kept as a pure helper so materials stay out of the core model.
 */
export function lampEmissiveFromBrightness(brightness: number): number {
  const b = clamp01(brightness);
  return 0.08 + b * 2.4;
}

/**
 * Analog needle local-Z angle (radians).
 * Dial ticks in virBench span roughly ±0.7; rest pose is slightly left (+).
 * Low reading → left (+), high reading → right (−).
 */
export const NEEDLE_ANGLE_MIN = 0.65;
export const NEEDLE_ANGLE_MAX = -0.65;

export function needleAngleFromNormalized(normalized: number): number {
  const t = clamp01(normalized);
  return NEEDLE_ANGLE_MIN + t * (NEEDLE_ANGLE_MAX - NEEDLE_ANGLE_MIN);
}

/** Current meter: 0 A → left, I_max → full scale. */
export function currentNeedleAngle(currentA: number): number {
  return needleAngleFromNormalized(currentA / CURRENT_MAX_A);
}

/**
 * Voltage meter: conceptual fixed source — needle sits near full scale for
 * the training voltage, with a tiny proportional cue if V is varied later.
 */
export function voltageNeedleAngle(
  voltageV: number,
  fullScaleV: number = CONCEPT_VOLTAGE_V,
): number {
  const scale = fullScaleV > 0 ? fullScaleV : CONCEPT_VOLTAGE_V;
  return needleAngleFromNormalized(voltageV / scale);
}

/** Lightweight mutable sim wrapper for station use (still Three-free). */
export class OhmsLawSim {
  private knobNormalized: number;
  private voltageV: number;
  private state: CircuitState;

  constructor(
    knobNormalized: number = DEFAULT_KNOB_NORMALIZED,
    voltageV: number = CONCEPT_VOLTAGE_V,
  ) {
    this.knobNormalized = clamp01(knobNormalized);
    this.voltageV = voltageV;
    this.state = evaluateFromKnob(this.knobNormalized, this.voltageV);
  }

  getKnobNormalized(): number {
    return this.knobNormalized;
  }

  getState(): CircuitState {
    return this.state;
  }

  setKnobNormalized(normalized: number): CircuitState {
    this.knobNormalized = clamp01(normalized);
    this.state = evaluateFromKnob(this.knobNormalized, this.voltageV);
    return this.state;
  }

  reset(): CircuitState {
    return this.setKnobNormalized(DEFAULT_KNOB_NORMALIZED);
  }
}
